import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import Layout from '../../../../components/Layout';
import { AuthUser } from '../../../../components/AuthUser';
import Pagination from 'react-responsive-pagination';

import { RequestPage } from '../../../../api/request';
import { Pedidos2 } from '../../../../api/requestPedidos';
import { getVendedor } from '../../../../api/request';
import { DetallePedido2 } from './DetallePedido2';

export const ConsultaPedidosCobranzas = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = AuthUser();

  const [Pedido, setPedido] = useState([]);
  const [vendedorMap, setVendedorMap] = useState({});
  const fetchIdRef = useRef(0); // incrementa por cada fetch, usamos para ignorar respuestas antiguas

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [urlPage, setUrlPage] = useState('');

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // filtros UI
  const [searchType, setSearchType] = useState(''); // Documento|Cliente|Vendedor|Fecha
  const [searchValue, setSearchValue] = useState('');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [currentFilters, setCurrentFilters] = useState({});

  // helper
  const buildSearchParams = (pageNum = 1, filters = {}) => {
    const params = new URLSearchParams();
    params.set('page', String(pageNum));
    if (filters.whatToSearch) params.set('whatToSearch', filters.whatToSearch);
    if (filters.Busqueda) params.set('Busqueda', filters.Busqueda);
    if (filters.fechaInicio) params.set('fechaInicio', filters.fechaInicio);
    if (filters.fechaFin) params.set('fechaFin', filters.fechaFin);
    if (filters.CodGerente) params.set('CodGerente', filters.CodGerente);
    return params.toString();
  };

  const fetchPedidos = async (pageNum = 1, filters = {}) => {
  setLoading(true);
  // identificar esta solicitud
  const reqId = ++fetchIdRef.current;

  try {
    const data = await Pedidos2('ALL', null, pageNum, filters);

    // si ya se lanzó otra petición después de esta, ignoramos esta respuesta
    if (fetchIdRef.current !== reqId) {
      return;
    }

    if (data) {
      setPage(data.current_page ?? data.meta?.current_page ?? 1);
      setTotalPages(data.last_page ?? data.meta?.last_page ?? 1);
      setUrlPage(data.first_page_url ?? data.links?.first ?? '');
      setCurrentPage(data.current_page ?? data.meta?.current_page ?? 1);

      const rows = data.data ?? [];
      setPedido(rows);
      setCurrentFilters(filters);

      // construir vendedorMap a partir de VendedorNombre devuelto por el backend
      const map = {};
      rows.forEach((r) => {
        const code = r.Vendedor ?? r.CodigoVendedor ?? r.CodVendedor ?? null;
        const name = r.VendedorNombre ?? r.Nombre ?? null;
        if (code) {
          map[code] = name ?? map[code] ?? null;
        }
      });
      setVendedorMap(map);
    } else {
      setPedido([]);
      setVendedorMap({});
    }
  } catch (err) {
    // si la respuesta es antigua, ya habremos salido arriba; aquí mostramos error solo si es la última
    if (fetchIdRef.current === reqId) {
      console.error('fetchPedidos error', err);
      setPedido([]);
      setVendedorMap({});
    }
  } finally {
    // solo apagamos loading si esta es la última petición
    if (fetchIdRef.current === reqId) {
      setLoading(false);
    }
  }
};



  // buscar códigos por nombre basados en vendedorMap
  const findCodigosByNombre = (query) => {
    if (!query) return [];
    const q = query.trim().toLowerCase();
    const res = [];
    Object.entries(vendedorMap).forEach(([codigo, nombre]) => {
      if (!nombre) return;
      if (String(nombre).toLowerCase().includes(q) || q.includes(String(nombre).toLowerCase())) res.push(codigo);
    });
    return res;
  };

  const onSearch = async () => {
  setNotFound(false);
  const filters = {};

  if (searchType === 'Fecha' && fechaInicio && fechaFin) {
    filters.fechaInicio = fechaInicio;
    filters.fechaFin = fechaFin;
    filters.whatToSearch = 'Fecha';
  } else if (searchType === 'Vendedor') {
    const codePattern = /^[Vv]?\d+$/;
    if (codePattern.test(searchValue.trim())) {
      filters.whatToSearch = 'Vendedor';
      filters.Busqueda = searchValue.trim();
    } else {
      // usamos el vendedorMap local para intentar resolver códigos
      const codes = findCodigosByNombre(searchValue);
      if (codes.length > 0) {
        filters.whatToSearch = 'Vendedor';
        filters.Busqueda = codes.join(',');
      } else {
        filters.whatToSearch = 'Vendedor';
        filters.Busqueda = searchValue.trim(); // fallback: backend buscará por nombre
      }
    }
  } else if (searchType) {
    filters.whatToSearch = searchType;
    filters.Busqueda = searchValue;
  }

  setCurrentFilters(filters);

  const qs = buildSearchParams(1, filters);
  // sólo navegamos: useEffect escucha location.search y hará fetchPedidos(1, filters)
  navigate({ pathname: location.pathname, search: `?${qs}` }, { replace: true });
};


  const clearFilters = async () => {
    setSearchType('');
    setSearchValue('');
    setFechaInicio('');
    setFechaFin('');
    setCurrentFilters({});
    setNotFound(false);
    navigate({ pathname: location.pathname, search: `?page=1` }, { replace: true });
    await fetchPedidos(1, {});
  };

  const handlePageChange = (pageNum) => {
    setCurrentPage(pageNum);
    const qs = buildSearchParams(pageNum, currentFilters);
    navigate({ pathname: location.pathname, search: `?${qs}` }, { replace: true });
    // useEffect will trigger fetchPedidos from location.search
  };

  // init + respond to URL changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const p = parseInt(params.get('page') || '1', 10);

    const filters = {};
    if (params.get('whatToSearch')) filters.whatToSearch = params.get('whatToSearch');
    if (params.get('Busqueda')) filters.Busqueda = params.get('Busqueda');
    if (params.get('fechaInicio')) filters.fechaInicio = params.get('fechaInicio');
    if (params.get('fechaFin')) filters.fechaFin = params.get('fechaFin');
    if (params.get('CodGerente')) filters.CodGerente = params.get('CodGerente');

    // sync UI
    setSearchType(filters.whatToSearch ?? (filters.fechaInicio ? 'Fecha' : ''));
    setSearchValue(filters.Busqueda ?? '');
    setFechaInicio(filters.fechaInicio ?? '');
    setFechaFin(filters.fechaFin ?? '');
    setCurrentFilters(filters);

    fetchPedidos(p, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  return (
    <Layout>
      {
        loading ? (
          <div style={{ position: 'fixed', left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}>
            <span className="loader-documentos"></span>
          </div>
        ) : <>
          <div className="consultas-title pt-5">
            <h4 className='bg-belmeny text-light px-5 rounded-pill'>Consulta de Pedidos</h4>
          </div>

          <h5 className="text-center mt-2 mb-4 text-belmeny"><i>
            {(user.CodGerente !== null) ? <>Usuario - {user.Nombre} - {user.CodVendedor}</> : <>Vendedor - {user.Nombre} - {user.CodVendedor}</>}
          </i></h5>

          {/* FILTROS */}
          <div className="container mb-3">
            <div className="row align-items-center gy-2">
              <div className="col-auto">
                <select className="form-select" value={searchType} onChange={(e) => {
                  setSearchType(e.target.value);
                  setSearchValue('');
                  setFechaInicio('');
                  setFechaFin('');
                }} style={{ minWidth: 180 }}>
                  <option value="">-- Filtro --</option>
                  <option value="Documento">Documento</option>
                  <option value="Cliente">Cliente</option>
                  <option value="Vendedor">Vendedor</option>
                  <option value="Fecha">Fecha</option>
                </select>
              </div>

              {searchType && searchType !== 'Fecha' ? (
                <div className="col-auto">
                  <input type="text" className="form-control" placeholder={`Buscar por ${searchType}`}
                    value={searchValue} onChange={(e) => setSearchValue(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') onSearch(); }} />
                </div>
              ) : null}

              {searchType === 'Fecha' ? <>
                <div className="col-auto"><input type="date" className="form-control" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} /></div>
                <div className="col-auto"><input type="date" className="form-control" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} /></div>
              </> : null}

              <div className="col-auto">
                <button className="btn btn-primary me-2" onClick={onSearch}>Buscar</button>
                <button className="btn btn-outline-secondary" onClick={clearFilters}>Limpiar</button>
              </div>

              <div className="col">
                {searchType ? <small className="text-muted">Filtro activo: <strong>{searchType}</strong> {searchType !== 'Fecha' && searchValue ? `— ${searchValue}` : ''} {searchType === 'Fecha' && fechaInicio && fechaFin ? `— ${fechaInicio} a ${fechaFin}` : ''}</small> : <small className="text-muted">Sin filtros</small>}
              </div>
            </div>
          </div>

          {/* TABLE */}
          <div className="container-fluid d-md-block d-none mb-3">
            <div className="rounded-0 my-2">
              {notFound ? <div className="alert alert-danger text-center"><h5>No se encontraron resultados</h5></div> : <>
                <table className='table table-bordered table-hover border-belmeny '>
                  <thead className='bg-belmeny text-light text-center'>
                    <tr>
                      <th>Pedido</th>
                      <th>Cliente</th>
                      {(user.CodGerente !== null) ? <th>Vendedor</th> : null}
                      <th>Monto</th>
                      <th>Fecha</th>
                      <th style={{ width: "15%" }}>Opciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(Pedido) && Pedido.length > 0 ? Pedido.map(item => (
                      <DetallePedido2 item={item} vendedor={ item.VendedorNombre ?? vendedorMap[item.Vendedor] ?? null } key={item.Documento} />
                    )) : <tr><td colSpan={(user.CodGerente !== null) ? 6 : 5} className="text-center py-4">No hay pedidos para mostrar.</td></tr>}
                  </tbody>
                </table>
              </>}
            </div>

            <Pagination total={totalPages} current={currentPage} onPageChange={page => handlePageChange(page)} />
          </div>

          {/* mobile */}
          <div className="container-fluid d-sm-block d-md-none">
            {Array.isArray(Pedido) && Pedido.length > 0 ? Pedido.map(item => (
              <DetallePedido2 item={item} vendedor={ item.VendedorNombre ?? vendedorMap[item.Vendedor] ?? null } key={item.Documento + 'Mobile'} />
            )) : <div className="text-center py-4">No hay pedidos para mostrar.</div>}
            <Pagination total={totalPages} current={currentPage} onPageChange={page => handlePageChange(page)} />
            <div className="division bg-belmeny my-3"></div>
          </div>
        </>}
    </Layout>
  );
};
